import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listCampuses } from '../../api/campusPortalApi';
import { listStudents } from '../../api/studentPortalApi';
import {
  listCourseGroups,
  getCourseGroup,
  updateCourseGroup,
  deleteCourseGroup,
  getCourseGroupTypes,
  listCourseGroupStudents,
  addStudentToCourseGroup,
  updateCourseGroupStudent,
  removeStudentFromCourseGroup,
  addCreditToStudent,
  uploadCreditAttachment,
  YEAR_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  getDayOfWeekLabel,
  getYearLabel,
  getTermLabel,
  isSpecialTerm
} from '../../api/courseGroupPortalApi';
import {
  canCreateGroup,
  canEditGroup,
  canDeleteCourseGroup,
  canFilterByCampus,
  // canOnlyEditCourseGroupOffset,
  canManageCourseGroupStudents
} from '../../utils/portalPermissions';
import { getDiscountOptionsForCampus } from '../../api/discountOptionsPortalApi';
import { getMyProfile } from '../../api/userProfileApi';
import TeacherAssignmentModal from '../../components/Portal/TeacherAssignmentModal';
import MaterialAssignmentModal from '../../components/Portal/MaterialAssignmentModal';
import { listTeachers } from '../../api/courseGroupTeacherApi';
import BatchStudentModal from '../../components/Portal/BatchStudentModal';
import FeedbackManagementModal from '../../components/Portal/FeedbackManagementModal';
import { formatSubjectsDisplay, listSubjectsCached } from '../../api/subjectPortalApi';
import * as S from './Management.styles';
import CourseGroupFormModal from './CourseGroupForm/CourseGroupFormModal';
import TermEndAdjustModal from '../../components/Portal/TermEndAdjustModal';
import { getTermEndAdjustCampuses } from '../../utils/termEndAdjust';
import {
  SlotPreviewContainer, SlotPreviewHeader, SlotPreviewRow,
  SlotRowMain, SlotRowSub, SlotBadge
} from './CourseGroupForm/CourseGroupForm.styles';

// Day filter chips container
const DayFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

// Day chip button
const DayChip = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid ${props => props.$active ? '#F1AF59' : '#d1d5db'};
  background: ${props => props.$active ? '#FEF3E2' : 'white'};
  color: ${props => props.$active ? '#D97706' : '#6b7280'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s;

  &:hover {
    border-color: #F1AF59;
  }
`;

// Clear all button for day chips
const ClearChipsButton = styled.button`
  padding: 4px 10px;
  border: none;
  border-radius: 12px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  margin-left: 8px;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

// Subject filter dropdown container
const SubjectFilterDropdown = styled.div`
  position: relative;
`;

// Subject filter button
const SubjectFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  min-width: 140px;
  justify-content: space-between;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }

  svg {
    width: 12px;
    height: 12px;
    transition: transform 0.2s;
  }

  &[data-open="true"] svg {
    transform: rotate(180deg);
  }
`;

// Subject dropdown content
const SubjectDropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 220px;
  max-height: 300px;
  overflow-y: auto;
  padding: 8px 0;
`;

// Subject option
const SubjectOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  padding-left: ${props => props.$isChild ? '32px' : '16px'};

  &:hover {
    background: #f3f4f6;
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #F1AF59;
  }
`;

// Subject filter actions
const SubjectFilterActions = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 4px;
`;

// Subject action button
const SubjectActionButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: ${props => props.$primary ? '#F1AF59' : '#f3f4f6'};
  color: ${props => props.$primary ? 'white' : '#6b7280'};

  &:hover {
    opacity: 0.9;
  }
`;

// Filter badge
const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #F1AF59;
  color: white;
  font-size: 11px;
  font-weight: 600;
`;

// Helper function to get short weekday label
const getShortDayLabel = (dayOfWeek) => {
  const shortLabels = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return shortLabels[dayOfWeek] || '';
};

// Helper function to combine weekdays for display
// Regular-term schedule rows with no main teacher: their materials are visible to no
// teacher outside relief weeks, so the list flags them.
const hasUnstaffedSchedule = (group) =>
  !isSpecialTerm(group.term) && (group.schedules || []).some(s => !s.teacherId);

const getWeekdaysDisplay = (schedules) => {
  if (!schedules || schedules.length === 0) return '';
  const uniqueDays = [...new Set(schedules.map(s => s.dayOfWeek))].sort((a, b) => a - b);
  return uniqueDays.map(d => getShortDayLabel(d)).join(', ');
};

// Helper function to get day of week from date string (for special term)
// Returns 1-7 (Monday-Sunday) to match DAY_OF_WEEK_OPTIONS
const getDayOfWeekFromDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  // JavaScript's getDay() returns 0 (Sun) to 6 (Sat)
  // Convert to 1 (Mon) to 7 (Sun)
  const jsDay = date.getDay();
  return jsDay === 0 ? 7 : jsDay;
};

// Helper function to format a date string as "YYYY-MM-DD (Weekday)"
const formatDateWithWeekday = (dateString) => {
  if (!dateString) return dateString;
  const date = new Date(`${dateString}T00:00:00`);
  if (isNaN(date.getTime())) return dateString;
  const jsDay = date.getDay();
  const dayOfWeek = jsDay === 0 ? 7 : jsDay;
  return `${dateString} (${getDayOfWeekLabel(dayOfWeek)})`;
};

// Helper function to get unique days from slots array (for special term filtering)
const getUniqueDaysFromSlots = (slots) => {
  if (!slots || slots.length === 0) return [];
  const days = slots
    .map(slot => getDayOfWeekFromDate(slot.date))
    .filter(day => day !== null);
  return [...new Set(days)].sort((a, b) => a - b);
};

// Helper function to get sorted slots with visual indices (for display)
// Sorts by date, then startTime, and assigns sequential visual indices starting from 1
const getSortedSlotsWithVisualIndex = (slots) => {
  if (!slots || slots.length === 0) return [];
  return [...slots]
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    })
    .map((slot, idx) => ({ ...slot, visualIndex: idx + 1 }));
};

const CourseGroups = () => {
  const { currentRole, currentUserId, currentUserCampus, currentUserCampusIds, updateCampus, updateCampusIds, hasRole } = usePortalRole();
  const location = useLocation();

  // Data state
  const [courseGroups, setCourseGroups] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courseGroupTypes, setCourseGroupTypes] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // 手机上列表/详情两屏切换：选中进详情、返回回列表
  const isMobile = useIsMobile();
  const [groupStudents, setGroupStudents] = useState([]);
  const [discountOptions, setDiscountOptions] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [campusFilter, setCampusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [termTypeFilter, setTermTypeFilter] = useState('all');
  const [dayOfWeekFilter, setDayOfWeekFilter] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState('all');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  // One-off changes made to this class from the timetable: weeks cancelled, moved or flagged.
  // They live in LessonPortal and are invisible to the recurrence fields above them, which is
  // exactly the confusion this list exists to remove.
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTermEndAdjust, setShowTermEndAdjust] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditEnrollment, setCreditEnrollment] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditAction, setCreditAction] = useState('add');
  const [creditDescription, setCreditDescription] = useState('');
  const [creditFile, setCreditFile] = useState(null);
  const [useStudentCredit, setUseStudentCredit] = useState(false);
  const [transferToStudent, setTransferToStudent] = useState(false);

  // New modal states for extended features
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showBatchStudentModal, setShowBatchStudentModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Teachers assigned to course group (for schedule slot assignment)
  const [assignedTeachers, setAssignedTeachers] = useState([]);

  const [studentFormData, setStudentFormData] = useState({
    studentId: '',
    discount: 0,
    startDate: '',
    absenceWeeks: ''
  });
  const [studentSearchQuery, setStudentSearchQuery] = useState('');

  // Load initial data
  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // For campus-restricted roles, get their campus first
        let userCampus = currentUserCampus;
        let userCampusIdsList = currentUserCampusIds;
        if (!canFilterByCampus(currentRole) && !userCampus) {
          try {
            const profile = await getMyProfile();
            userCampusIdsList = profile?.campusIds || (profile?.campusId ? [profile.campusId] : []);
            userCampus = userCampusIdsList.length ? userCampusIdsList[0] : null;
            if (userCampus) {
              updateCampus(userCampus);
            }
            if (userCampusIdsList.length) {
              updateCampusIds(userCampusIdsList);
            }
          } catch (profileErr) {
            console.warn('Could not fetch user profile:', profileErr);
          }
        }

        // Set campus filter for restricted users
        if (!canFilterByCampus(currentRole) && userCampus) {
          // For users with multiple campuses, default to 'all' (their campuses)
          if (userCampusIdsList.length > 1) {
            setCampusFilter('all');
          } else {
            setCampusFilter(userCampus);
          }
        }

        // The full student list is only needed by the Add Student picker, so it
        // loads in the background instead of holding up the course group list.
        setStudentsLoading(true);
        listStudents()
          .catch(() => [])
          .then(studentsData => {
            if (!cancelled) setAllStudents(studentsData);
          })
          .finally(() => {
            if (!cancelled) setStudentsLoading(false);
          });

        const [groupsData, campusesData, typesData, subjectsData] = await Promise.all([
          listCourseGroups(),
          listCampuses(),
          getCourseGroupTypes(),
          listSubjectsCached().catch(() => [])
        ]);

        setCourseGroups(groupsData);
        setCampuses(campusesData);
        setCourseGroupTypes(typesData);
        setAllSubjects(subjectsData);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [currentRole, currentUserCampus, currentUserCampusIds, updateCampus, updateCampusIds]);

  // Load students, discount options, and teachers (for special term) when group is selected
  useEffect(() => {
    const loadGroupData = async () => {
      if (!selectedGroup) {
        setGroupStudents([]);
        setDiscountOptions([]);
        setAssignedTeachers([]);
        return;
      }

      try {
        const promises = [
          listCourseGroupStudents(selectedGroup.id),
          getDiscountOptionsForCampus(selectedGroup.campusId).catch(() => [])
        ];

        // Load assigned teachers for special term (needed for slot display)
        if (isSpecialTerm(selectedGroup.term)) {
          promises.push(listTeachers(selectedGroup.id).catch(() => []));
        }

        const results = await Promise.all(promises);
        setGroupStudents(results[0]);
        setDiscountOptions(results[1]);
        if (isSpecialTerm(selectedGroup.term)) {
          setAssignedTeachers(results[2] || []);
        }
      } catch (err) {
        console.error('Error loading group data:', err);
      }
    };

    loadGroupData();
  }, [selectedGroup]);

  // Handle navigation state (pre-select course group when navigated from timetable)
  useEffect(() => {
    if (location.state?.selectedCourseGroupId && courseGroups.length > 0) {
      const group = courseGroups.find(g => g.id === location.state.selectedCourseGroupId);
      if (group) {
        setSelectedGroup(group);
        // Clear the navigation state to avoid re-selecting on future renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, courseGroups]);

  // Close subject dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSubjectDropdown && !event.target.closest('[data-subject-filter]')) {
        setShowSubjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSubjectDropdown]);

  // Filter course groups
  const filteredGroups = useMemo(() => {
    let groups = courseGroups;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      groups = groups.filter(group =>
        group.title?.toLowerCase().includes(term) ||
        group.campusName?.toLowerCase().includes(term) ||
        group.typeDisplayName?.toLowerCase().includes(term) ||
        getYearLabel(group.year).toLowerCase().includes(term)
      );
    }

    // Apply campus filter
    if (campusFilter !== 'all') {
      groups = groups.filter(group => group.campusId === campusFilter);
    } else if (!canFilterByCampus(currentRole) && currentUserCampusIds.length > 0) {
      // Campus-restricted roles: only show groups from their assigned campuses
      groups = groups.filter(group => currentUserCampusIds.includes(group.campusId));
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      groups = groups.filter(group => group.typeId === typeFilter);
    }

    // Apply term type filter (regular vs special)
    if (termTypeFilter === 'regular') {
      groups = groups.filter(group => !isSpecialTerm(group.term));
    } else if (termTypeFilter === 'special') {
      groups = groups.filter(group => isSpecialTerm(group.term));
    }

    // Apply day of week filter
    if (dayOfWeekFilter.length > 0) {
      groups = groups.filter(group => {
        if (isSpecialTerm(group.term)) {
          // For special term: check if any slot falls on selected days
          const slotDays = getUniqueDaysFromSlots(group.slots);
          return dayOfWeekFilter.some(day => slotDays.includes(day));
        } else {
          // For regular term: check schedules
          if (!group.schedules || group.schedules.length === 0) return false;
          const scheduleDays = group.schedules.map(s => s.dayOfWeek);
          return dayOfWeekFilter.some(day => scheduleDays.includes(day));
        }
      });
    }

    // Apply subject filter
    if (subjectFilter.length > 0) {
      groups = groups.filter(group => {
        const groupSubjectIds = [];
        (group.subjects || []).forEach(s => {
          groupSubjectIds.push(s.subjectId);
          (s.children || []).forEach(c => groupSubjectIds.push(c.subjectId));
        });
        return subjectFilter.some(id => groupSubjectIds.includes(id));
      });
    }

    // Apply year filter
    if (yearFilter !== 'all') {
      const yearValue = parseInt(yearFilter, 10);
      groups = groups.filter(group => group.year === yearValue);
    }

    // Sort by: time (course schedule) > year/grade > room
    groups.sort((a, b) => {
      // 1. Time sort
      const getTimeSortKey = (group) => {
        if (isSpecialTerm(group.term)) {
          if (!group.slots?.length) return '9999-99-99 99:99';
          const sorted = [...group.slots].sort((s1, s2) =>
            (s1.date || '').localeCompare(s2.date || '') || (s1.startTime || '').localeCompare(s2.startTime || '')
          );
          return `${sorted[0].date || '9999-99-99'} ${sorted[0].startTime || '99:99'}`;
        } else {
          if (!group.schedules?.length) return '9999-99-99 99:99';
          const sorted = [...group.schedules].sort((s1, s2) =>
            (s1.dayOfWeek - s2.dayOfWeek) || (s1.startTime || '').localeCompare(s2.startTime || '')
          );
          return `0000-00-0${sorted[0].dayOfWeek} ${sorted[0].startTime || '99:99'}`;
        }
      };
      const cmpTime = getTimeSortKey(a).localeCompare(getTimeSortKey(b));
      if (cmpTime !== 0) return cmpTime;

      // 2. Year/grade sort (ascending: -1 Kindy, 0 Pre-primary, 1-12)
      const cmpYear = (a.year ?? 9999) - (b.year ?? 9999);
      if (cmpYear !== 0) return cmpYear;

      // 3. Room sort (alphabetical)
      return (a.room || '').toLowerCase().localeCompare((b.room || '').toLowerCase());
    });

    return groups;
  }, [courseGroups, searchTerm, campusFilter, typeFilter, termTypeFilter, dayOfWeekFilter, subjectFilter, yearFilter, currentRole, currentUserCampusIds]);

  // // Get campus name
  // const getCampusName = useCallback((campusId) => {
  //   const campus = campuses.find(c => c.id === campusId);
  //   return campus ? campus.name : '';
  // }, [campuses]);

  // Term End Adjust: only campuses with the CampusPortal.termEndAdjust flag that this user runs
  const termEndAdjustCampuses = useMemo(
    () => getTermEndAdjustCampuses(campuses, {
      isSystemAdmin: hasRole('system_admin') || hasRole('developer'),
      userCampusIds: currentUserCampusIds
    }),
    [campuses, hasRole, currentUserCampusIds]
  );
  const canTermEndAdjust = termEndAdjustCampuses.length > 0 &&
    (hasRole('system_admin') || hasRole('developer') || hasRole('manager') || hasRole('admin'));

  // Refresh course groups
  const refreshCourseGroups = async () => {
    try {
      const groupsData = await listCourseGroups();
      setCourseGroups(groupsData);
    } catch (err) {
      console.error('Error refreshing course groups:', err);
    }
  };

  // Handle teacher added - auto-assign to matching unassigned slots
  const handleTeacherAdded = async ({ teacherId, subjectIds }) => {
    if (!selectedGroup || !subjectIds?.length) return;

    const isSpecial = isSpecialTerm(selectedGroup.term);
    let hasChanges = false;

    try {
      if (isSpecial && selectedGroup.slots?.length) {
        // For special term: update slots
        const updatedSlots = selectedGroup.slots.map(slot => {
          // Skip if already has teacher or no subject or subject doesn't match
          if (slot.teacherId || !slot.subjectId || !subjectIds.includes(slot.subjectId)) {
            return slot;
          }
          hasChanges = true;
          return { ...slot, teacherId };
        });

        if (hasChanges) {
          await updateCourseGroup(selectedGroup.id, { slots: updatedSlots });
        }
      } else if (!isSpecial && selectedGroup.schedules?.length) {
        // For regular term: update schedules
        const updatedSchedules = selectedGroup.schedules.map(schedule => {
          // Skip if already has teacher or no subject or subject doesn't match
          const bookingSubjects = subjectIdsOf(schedule);
          if (schedule.teacherId || !bookingSubjects.length
              || !bookingSubjects.every(sid => subjectIds.includes(sid))) {
            return schedule;
          }
          hasChanges = true;
          return { ...schedule, teacherId };
        });

        if (hasChanges) {
          await updateCourseGroup(selectedGroup.id, { schedules: updatedSchedules });
        }
      }

      // Refresh data to show updated assignments
      if (hasChanges) {
        const updatedGroup = await getCourseGroup(selectedGroup.id);
        setSelectedGroup(updatedGroup);
        await refreshCourseGroups();
      }
    } catch (err) {
      console.error('Error auto-assigning teacher to slots:', err);
      // Don't alert - the teacher was added successfully, just auto-assign failed
    }
  };

  // Refresh the selected group and list after a relief assignment is set/cleared, so the
  // Teachers modal (schedules[].reliefAssignments) and the course group list stay in sync.
  const handleReliefChange = async () => {
    if (!selectedGroup) return;
    try {
      const updatedGroup = await getCourseGroup(selectedGroup.id);
      setSelectedGroup(updatedGroup);
      await refreshCourseGroups();
    } catch (err) {
      console.error('Error refreshing after relief change:', err);
    }
  };

  // Handle delete course group
  const handleDelete = async () => {
    if (!selectedGroup) return;
    if (!window.confirm(`Are you sure you want to delete this course group? This will also remove all enrolled students.`)) return;

    try {
      setActionLoading(true);
      await deleteCourseGroup(selectedGroup.id);
      setSelectedGroup(null);
      await refreshCourseGroups();
    } catch (err) {
      console.error('Error deleting course group:', err);
      alert('Failed to delete course group: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle add student to course group
  const handleAddStudent = async () => {
    if (!selectedGroup || !studentFormData.studentId) {
      alert('Please select a student');
      return;
    }

    try {
      setActionLoading(true);

      const absenceWeeksArray = studentFormData.absenceWeeks
        ? studentFormData.absenceWeeks.split(',').map(w => parseInt(w.trim())).filter(w => !isNaN(w))
        : [];

      await addStudentToCourseGroup({
        courseGroupId: selectedGroup.id,
        studentId: studentFormData.studentId,
        discount: parseFloat(studentFormData.discount) || 0,
        startDate: studentFormData.startDate || selectedGroup.startDate,
        absenceWeeks: absenceWeeksArray
      });

      setShowStudentModal(false);
      resetStudentForm();

      // Refresh students
      const students = await listCourseGroupStudents(selectedGroup.id);
      setGroupStudents(students);
    } catch (err) {
      console.error('Error adding student:', err);
      alert('Failed to add student: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update student enrollment
  const handleUpdateEnrollment = async () => {
    if (!editingEnrollment) return;

    try {
      setActionLoading(true);

      const absenceWeeksArray = studentFormData.absenceWeeks
        ? studentFormData.absenceWeeks.split(',').map(w => parseInt(w.trim())).filter(w => !isNaN(w))
        : [];

      await updateCourseGroupStudent(editingEnrollment.id, {
        discount: parseFloat(studentFormData.discount) || 0,
        startDate: studentFormData.startDate,
        absenceWeeks: absenceWeeksArray
      });

      setEditingEnrollment(null);
      setShowStudentModal(false);
      resetStudentForm();

      // Refresh students
      const students = await listCourseGroupStudents(selectedGroup.id);
      setGroupStudents(students);
    } catch (err) {
      console.error('Error updating enrollment:', err);
      alert('Failed to update enrollment: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle add credit to student
  const handleAddCredit = async () => {
    if (!creditEnrollment) return;

    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid positive amount');
      return;
    }

    // Description is required
    if (!creditDescription.trim()) {
      alert('Please enter a description for this credit adjustment');
      return;
    }

    // Validate transfer options
    if (creditAction === 'add' && useStudentCredit) {
      if (amount > (creditEnrollment.studentCredit || 0)) {
        alert(`Insufficient student balance. Available: $${(creditEnrollment.studentCredit || 0).toFixed(2)}`);
        return;
      }
    }

    if (creditAction === 'subtract' && transferToStudent) {
      if (amount > (creditEnrollment.remainingCredits || 0)) {
        alert(`Can only transfer up to remaining credit: $${(creditEnrollment.remainingCredits || 0).toFixed(2)}`);
        return;
      }
    }

    const delta = creditAction === 'add' ? amount : -amount;

    try {
      setActionLoading(true);

      // Call API with transfer options
      const result = await addCreditToStudent(
        creditEnrollment.id,
        delta,
        creditDescription.trim(),
        {
          useStudentCredit: creditAction === 'add' && useStudentCredit,
          transferToStudent: creditAction === 'subtract' && transferToStudent,
          filename: creditFile?.name,
          contentType: creditFile?.type
        }
      );

      // Upload file to S3 if presigned URL was returned
      if (result.uploadUrl && creditFile) {
        await uploadCreditAttachment(result.uploadUrl, creditFile, creditFile.type);
      }

      setShowCreditModal(false);
      setCreditEnrollment(null);
      setCreditAmount('');
      setCreditAction('add');
      setCreditDescription('');
      setCreditFile(null);
      setUseStudentCredit(false);
      setTransferToStudent(false);

      // Refresh students
      const students = await listCourseGroupStudents(selectedGroup.id);
      setGroupStudents(students);
    } catch (err) {
      console.error('Error adding credit:', err);
      alert('Failed to update credit: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle remove student
  const handleRemoveStudent = async (enrollment) => {
    if (!window.confirm(`Remove ${enrollment.studentName} from this course group?`)) return;

    try {
      setActionLoading(true);
      await removeStudentFromCourseGroup(enrollment.id);

      // Refresh students
      const students = await listCourseGroupStudents(selectedGroup.id);
      setGroupStudents(students);
    } catch (err) {
      console.error('Error removing student:', err);
      alert('Failed to remove student: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Reset student form
  const resetStudentForm = () => {
    setStudentFormData({
      studentId: '',
      discount: 0,
      startDate: '',
      absenceWeeks: ''
    });
    setStudentSearchQuery('');
  };

  /**
   * What a class created from this page starts out as.
   *
   * Only the campus, because that is the one thing the page knows and the form cannot guess:
   * a campus-restricted user has exactly one, and a filtered list says which one is being
   * looked at. The timetable passes a day and a time here as well, since a click on an empty
   * cell means both.
   */
  const createPrefill = useMemo(() => {
    if (canFilterByCampus(currentRole)) return {};
    if (campusFilter && campusFilter !== 'all') return { campusId: campusFilter };
    if (currentUserCampusIds.length === 1) return { campusId: currentUserCampusIds[0] };
    return {};
  }, [currentRole, campusFilter, currentUserCampusIds]);

  const openCreateModal = () => setShowCreateModal(true);

  // Open edit modal. The form reads the group itself; the teacher list is loaded here because
  // the detail panel behind the modal names teachers too.
  const openEditModal = async () => {
    if (!selectedGroup) return;
    try {
      setAssignedTeachers(await listTeachers(selectedGroup.id));
    } catch (err) {
      console.error('Error loading assigned teachers:', err);
      setAssignedTeachers([]);
    }
    setShowEditModal(true);
  };

  const closeFormModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
  };

  /** After the shared form writes: re-read the group being looked at, then the list. */
  const handleFormSaved = async (saved) => {
    if (saved?.id) {
      try {
        setSelectedGroup(await getCourseGroup(saved.id));
      } catch (err) {
        console.error('Error reloading course group:', err);
      }
    }
    await refreshCourseGroups();
  };

  // Open student modal for adding
  const openAddStudentModal = () => {
    resetStudentForm();
    setEditingEnrollment(null);
    setShowStudentModal(true);
  };

  // Open student modal for editing
  const openEditStudentModal = (enrollment) => {
    setStudentFormData({
      studentId: enrollment.studentId,
      discount: enrollment.discount || 0,
      startDate: enrollment.startDate || '',
      absenceWeeks: (enrollment.absenceWeeks || []).join(', ')
    });
    setEditingEnrollment(enrollment);
    setShowStudentModal(true);
  };

  // Open credit modal
  const openCreditModal = (enrollment) => {
    setCreditEnrollment(enrollment);
    setCreditAmount('');
    setCreditAction('add');
    setCreditDescription('');
    setCreditFile(null);
    setUseStudentCredit(false);
    setTransferToStudent(false);
    setShowCreditModal(true);
  };

  /**
   * The subjects taught in one booking.
   *
   * A booking covers one subject when the block is split by time and several when it is not,
   * so this is a list. `subjectId` is read as a fallback for rows loaded before the migration.
   */
  const subjectIdsOf = (row) => {
    const ids = (row.subjectIds || []).filter(Boolean);
    if (ids.length) return ids;
    return row.subjectId ? [row.subjectId] : [];
  };

  // Get available students (not yet enrolled)
  const availableStudents = useMemo(() => {
    const enrolledIds = groupStudents.map(e => e.studentId);
    return allStudents.filter(s => !enrolledIds.includes(s.id));
  }, [allStudents, groupStudents]);

  // Get subject name from ID (for slot display in detail panel)
  const getSubjectNameById = useCallback((subjectId) => {
    if (!subjectId || !selectedGroup?.subjects) return null;
    const subject = selectedGroup.subjects.find(s => s.subjectId === subjectId);
    return subject?.subjectName || null;
  }, [selectedGroup?.subjects]);

  // Get teacher name from ID (for slot display in detail panel)
  const getTeacherNameById = useCallback((teacherId) => {
    if (!teacherId) return null;
    const teacher = assignedTeachers.find(t => t.teacherId === teacherId);
    return teacher?.teacherName || null;
  }, [assignedTeachers]);

  if (loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Course Groups</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  if (error) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Course Groups</S.Title>
            <S.Subtitle>Error loading data</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>{error}</S.ErrorBanner>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Course Groups</S.Title>
          <S.Subtitle>Manage course groups and student enrollments</S.Subtitle>
        </div>
        <S.ButtonGroup>
          {canTermEndAdjust && (
            <S.Button color="#dc2626" onClick={() => setShowTermEndAdjust(true)}>
              Term End Adjust
            </S.Button>
          )}
          {canCreateGroup(currentRole) && (
            <S.Button color="#F1AF59" onClick={openCreateModal}>
              + Create Course Group
            </S.Button>
          )}
        </S.ButtonGroup>
      </S.Header>

      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search course groups..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <S.FilterSelect
          value={termTypeFilter}
          onChange={(e) => setTermTypeFilter(e.target.value)}
        >
          <option value="all">All Terms</option>
          <option value="regular">Regular (T1-T4)</option>
          <option value="special">Special (T0)</option>
        </S.FilterSelect>
        <SubjectFilterDropdown data-subject-filter>
          <SubjectFilterButton
            onClick={() => setShowSubjectDropdown(!showSubjectDropdown)}
            data-open={showSubjectDropdown}
          >
            <span>
              {subjectFilter.length === 0
                ? 'All Subjects'
                : `${subjectFilter.length} Subject${subjectFilter.length > 1 ? 's' : ''}`}
            </span>
            {subjectFilter.length > 0 && (
              <FilterBadge>{subjectFilter.length}</FilterBadge>
            )}
            <svg viewBox="0 0 12 12" fill="currentColor">
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          </SubjectFilterButton>
          {showSubjectDropdown && (
            <SubjectDropdownContent>
              {allSubjects.map(subject => (
                <React.Fragment key={subject.id}>
                  <SubjectOption>
                    <input
                      type="checkbox"
                      checked={subjectFilter.includes(subject.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSubjectFilter([...subjectFilter, subject.id]);
                        } else {
                          setSubjectFilter(subjectFilter.filter(id => id !== subject.id));
                        }
                      }}
                    />
                    <span>{subject.subjectName}</span>
                  </SubjectOption>
                  {(subject.children || []).map(child => (
                    <SubjectOption key={child.id} $isChild>
                      <input
                        type="checkbox"
                        checked={subjectFilter.includes(child.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSubjectFilter([...subjectFilter, child.id]);
                          } else {
                            setSubjectFilter(subjectFilter.filter(id => id !== child.id));
                          }
                        }}
                      />
                      <span>{child.subjectName}</span>
                    </SubjectOption>
                  ))}
                </React.Fragment>
              ))}
              <SubjectFilterActions>
                <SubjectActionButton
                  onClick={() => {
                    const allIds = [];
                    allSubjects.forEach(s => {
                      allIds.push(s.id);
                      (s.children || []).forEach(c => allIds.push(c.id));
                    });
                    setSubjectFilter(allIds);
                  }}
                >
                  Select All
                </SubjectActionButton>
                <SubjectActionButton
                  onClick={() => {
                    setSubjectFilter([]);
                    setShowSubjectDropdown(false);
                  }}
                >
                  Clear
                </SubjectActionButton>
              </SubjectFilterActions>
            </SubjectDropdownContent>
          )}
        </SubjectFilterDropdown>
        <S.FilterSelect
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          {courseGroupTypes.map(type => (
            <option key={type.id} value={type.id}>{type.displayName}</option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
        >
          <option value="all">All Years</option>
          {YEAR_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </S.FilterSelect>
        {canFilterByCampus(currentRole) ? (
          <S.FilterSelect
            value={campusFilter}
            onChange={(e) => setCampusFilter(e.target.value)}
          >
            <option value="all">All Campuses</option>
            {campuses.map(campus => (
              <option key={campus.id} value={campus.id}>{campus.name}</option>
            ))}
          </S.FilterSelect>
        ) : currentUserCampusIds.length > 1 && (
          <S.FilterSelect
            value={campusFilter}
            onChange={(e) => setCampusFilter(e.target.value)}
          >
            <option value="all">All My Campuses</option>
            {campuses.filter(c => currentUserCampusIds.includes(c.id)).map(campus => (
              <option key={campus.id} value={campus.id}>{campus.name}</option>
            ))}
          </S.FilterSelect>
        )}
      </S.FiltersBar>

      {/* Day of Week Chips Filter */}
      <DayFilterRow>
        {DAY_OF_WEEK_OPTIONS.map(day => (
          <DayChip
            key={day.value}
            $active={dayOfWeekFilter.includes(day.value)}
            onClick={() => {
              if (dayOfWeekFilter.includes(day.value)) {
                setDayOfWeekFilter(dayOfWeekFilter.filter(d => d !== day.value));
              } else {
                setDayOfWeekFilter([...dayOfWeekFilter, day.value].sort((a, b) => a - b));
              }
            }}
          >
            {day.label.substring(0, 3)}
          </DayChip>
        ))}
        {dayOfWeekFilter.length > 0 && (
          <ClearChipsButton onClick={() => setDayOfWeekFilter([])}>
            Clear Days
          </ClearChipsButton>
        )}
      </DayFilterRow>

      {filteredGroups.length === 0 ? (
        <S.EmptyState style={{ marginTop: '40px', padding: '60px' }}>
          {searchTerm
            ? `No course groups found matching "${searchTerm}"`
            : 'No course groups available. Create one to get started.'}
        </S.EmptyState>
      ) : (
        <S.SplitLayout>
          {(!isMobile || !selectedGroup) && (
          <S.GroupList>
            {filteredGroups.map(group => (
              <S.GroupCard
                key={group.id}
                onClick={() => setSelectedGroup(group)}
                $isSelected={selectedGroup?.id === group.id}
              >
                <S.GroupCardHeader>
                  <S.GroupName>
                    {!isSpecialTerm(group.term) && group.schedules?.length > 0
                      ? `${getWeekdaysDisplay(group.schedules)} | ${group.typeDisplayName || 'Group'} | ${group.title || '-'}`
                      : (group.typeDisplayName ? `${group.typeDisplayName} | ${group.title || '-'}` : (group.title || '-'))
                    }
                  </S.GroupName>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {group.capacityWarning && (
                      <S.Badge color="#f59e0b">Over Capacity</S.Badge>
                    )}
                    {hasUnstaffedSchedule(group) && (
                      <S.Badge color="#dc2626">No Teacher</S.Badge>
                    )}
                    {isSpecialTerm(group.term) ? (
                      <S.Badge color="#8b5cf6">
                        {group.slots?.length || 0} slots
                      </S.Badge>
                    ) : (
                      <S.Badge color={group.isHoliday ? '#f59e0b' : '#10b981'}>
                        {group.isHoliday ? 'Holiday' : `Week ${group.currentWeek || 0}`}
                      </S.Badge>
                    )}
                  </div>
                </S.GroupCardHeader>
                <S.GroupInfo>
                  <div>{getTermLabel(group.term)} | {group.campusName}</div>
                  <div>Y{group.year} | {formatSubjectsDisplay(group.subjects)}</div>
                  <div>{group.startDate} to {group.finishDate}</div>
                  {!isSpecialTerm(group.term) && group.schedules?.map((schedule, idx) => (
                    <div key={idx}>
                      {getDayOfWeekLabel(schedule.dayOfWeek)} {schedule.startTime} - {schedule.endTime}
                    </div>
                  ))}
                  {isSpecialTerm(group.term) && (
                    <div style={{ color: '#8b5cf6' }}>
                      Special term: {group.slots?.length || 0} class sessions
                    </div>
                  )}
                  {group.teacherNames?.length > 0 && (
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>
                      Teacher: {group.teacherNames.join(', ')}
                    </div>
                  )}
                  <div>
                    {group.totalStudents || 0}{group.capacityLimit ? `/${group.capacityLimit}` : ''} students | ${group.creditPerClass}/h
                  </div>
                </S.GroupInfo>
              </S.GroupCard>
            ))}
          </S.GroupList>
          )}

          {(!isMobile || selectedGroup) && (
          <S.DetailPanel>
            {isMobile && selectedGroup && (
              <button
                type="button"
                onClick={() => setSelectedGroup(null)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  marginBottom: '12px', padding: '10px 14px', minHeight: '44px',
                  border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff',
                  color: '#374151', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                ← 返回列表
              </button>
            )}
            {selectedGroup ? (
              <>
                <S.DetailHeader>
                  <S.Title>
                    {selectedGroup.typeDisplayName && `${selectedGroup.typeDisplayName} | `}
                    {selectedGroup.title || '-'}
                  </S.Title>
                  <S.ButtonGroup>
                    {canManageCourseGroupStudents(currentRole) && (
                      <>
                        <S.Button color="#3b82f6" onClick={() => setShowTeacherModal(true)}>
                          Teachers
                        </S.Button>
                        <S.Button color="#10b981" onClick={() => setShowMaterialModal(true)}>
                          Materials
                        </S.Button>
                        <S.Button color="#f59e0b" onClick={() => setShowFeedbackModal(true)}>
                          Feedbacks
                        </S.Button>
                      </>
                    )}
                    {canEditGroup(currentRole, selectedGroup, currentUserId, currentUserCampus, currentUserCampusIds) && (
                      <S.Button color="#F1AF59" onClick={openEditModal}>
                        Edit
                      </S.Button>
                    )}
                    {canDeleteCourseGroup(currentRole) && (
                      <S.Button color="#ef4444" onClick={handleDelete} disabled={actionLoading}>
                        Delete
                      </S.Button>
                    )}
                  </S.ButtonGroup>
                </S.DetailHeader>

                <S.Card>
                  <S.CardHeader>
                    <h3>Course Information</h3>
                  </S.CardHeader>
                  <S.InfoGrid>
                    <S.InfoItem>
                      <S.InfoLabel>Type</S.InfoLabel>
                      <S.InfoValue>
                        {selectedGroup.typeDisplayName || '-'}
                        {selectedGroup.capacityWarning && (
                          <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
                            (Capacity: {selectedGroup.totalStudents}/{selectedGroup.capacityLimit})
                          </span>
                        )}
                      </S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Title</S.InfoLabel>
                      <S.InfoValue>{selectedGroup.title || '-'}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Subjects</S.InfoLabel>
                      <S.InfoValue>{formatSubjectsDisplay(selectedGroup.subjects)}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Year</S.InfoLabel>
                      <S.InfoValue>{getYearLabel(selectedGroup.year)}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Term</S.InfoLabel>
                      <S.InfoValue>{getTermLabel(selectedGroup.term)}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Campus</S.InfoLabel>
                      <S.InfoValue>{selectedGroup.campusName}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Start Date</S.InfoLabel>
                      <S.InfoValue>{formatDateWithWeekday(selectedGroup.startDate)}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Finish Date</S.InfoLabel>
                      <S.InfoValue>{formatDateWithWeekday(selectedGroup.finishDate)}</S.InfoValue>
                    </S.InfoItem>
                    {!isSpecialTerm(selectedGroup.term) && (
                      <S.InfoItem>
                        <S.InfoLabel>Current Week</S.InfoLabel>
                        <S.InfoValue>
                          {selectedGroup.isHoliday ? 'Holiday Week' : `Week ${selectedGroup.currentWeek || 0}`}
                          {selectedGroup.offset !== 0 && ` (Offset: ${selectedGroup.offset} days)`}
                        </S.InfoValue>
                      </S.InfoItem>
                    )}
                    {isSpecialTerm(selectedGroup.term) && (
                      <S.InfoItem>
                        <S.InfoLabel>Slots</S.InfoLabel>
                        <S.InfoValue>
                          {selectedGroup.slots?.length || 0} class sessions
                        </S.InfoValue>
                      </S.InfoItem>
                    )}
                    <S.InfoItem>
                      <S.InfoLabel>Hourly Rate</S.InfoLabel>
                      <S.InfoValue>${selectedGroup.creditPerClass}/hr</S.InfoValue>
                    </S.InfoItem>
                    {!isSpecialTerm(selectedGroup.term) && selectedGroup.holidayWeeks?.length > 0 && (
                      <S.InfoItem>
                        <S.InfoLabel>Holiday Weeks</S.InfoLabel>
                        <S.InfoValue>{selectedGroup.holidayWeeks.join(', ')}</S.InfoValue>
                      </S.InfoItem>
                    )}
                  </S.InfoGrid>
                </S.Card>

                <S.Card style={{ marginTop: '24px' }}>
                  <S.CardHeader>
                    <h3>{isSpecialTerm(selectedGroup.term) ? 'Class Slots' : 'Schedule'}</h3>
                  </S.CardHeader>
                  <div style={{ padding: '16px 0' }}>
                    {/* Regular term schedules */}
                    {!isSpecialTerm(selectedGroup.term) && (
                      <>
                        {selectedGroup.schedules?.map((schedule, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '12px 20px',
                              background: idx % 2 === 0 ? '#f9fafb' : 'white',
                              borderRadius: '6px'
                            }}
                          >
                            <span style={{ fontWeight: '600' }}>{getDayOfWeekLabel(schedule.dayOfWeek)}</span>
                            <span>
                              {schedule.startTime} - {schedule.endTime}
                              {!schedule.teacherId && (
                                <span style={{ marginLeft: '8px', color: '#dc2626', fontWeight: 600 }}>
                                  No main teacher
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                        {(!selectedGroup.schedules || selectedGroup.schedules.length === 0) && (
                          <S.EmptyState>No schedule defined</S.EmptyState>
                        )}
                      </>
                    )}

                    {/* Special term slots */}
                    {isSpecialTerm(selectedGroup.term) && (
                      <>
                        {selectedGroup.slots?.length > 0 ? (
                          <SlotPreviewContainer style={{ maxHeight: '250px' }}>
                            <SlotPreviewHeader>
                              <span>Slot</span>
                              <span>Date</span>
                              <span>Time</span>
                              <span></span>
                            </SlotPreviewHeader>
                            {getSortedSlotsWithVisualIndex(selectedGroup.slots).map((slot, idx) => (
                              <SlotPreviewRow key={slot.slotIndex || idx} $isEven={idx % 2 === 0}>
                                <SlotRowMain>
                                  <SlotBadge>#{slot.visualIndex}</SlotBadge>
                                  <span>{slot.date}</span>
                                  <span>{slot.startTime} - {slot.endTime}</span>
                                  <span></span>
                                </SlotRowMain>
                                <SlotRowSub>
                                  <span>Subject: {getSubjectNameById(slot.subjectId) || '-'}</span>
                                  <span>Teacher: {getTeacherNameById(slot.teacherId) || '-'}</span>
                                </SlotRowSub>
                              </SlotPreviewRow>
                            ))}
                          </SlotPreviewContainer>
                        ) : (
                          <S.EmptyState>No class slots defined</S.EmptyState>
                        )}
                      </>
                    )}
                  </div>
                </S.Card>

                <S.Card style={{ marginTop: '24px' }}>
                  <S.CardHeader>
                    <h3>Students ({groupStudents.length})</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {canManageCourseGroupStudents(currentRole) && (
                        <>
                          <S.SmallButton color="#8b5cf6" onClick={() => setShowBatchStudentModal(true)}>
                            Batch Add
                          </S.SmallButton>
                          <S.SmallButton color="#F1AF59" onClick={openAddStudentModal}>
                            + Add Student
                          </S.SmallButton>
                        </>
                      )}
                    </div>
                  </S.CardHeader>
                  <S.StudentList>
                    {groupStudents.map(enrollment => (
                      <S.StudentItem key={enrollment.id}>
                        <div>
                          <S.StudentName>{enrollment.studentName}</S.StudentName>
                          <S.StudentDetail>
                            {getYearLabel(enrollment.studentGrade)} | Start Week: {enrollment.startWeek}
                            {enrollment.discount > 0 && ` | Discount: ${enrollment.discount}%`}
                          </S.StudentDetail>
                          <S.StudentDetail>
                            Credit: ${enrollment.credit} | Required: ${enrollment.totalCreditsUsed} | Remaining: ${enrollment.remainingCredits}
                            {enrollment.studentCredit > 0 && ` | Student Balance: $${enrollment.studentCredit}`}
                          </S.StudentDetail>
                          {enrollment.absenceWeeks?.length > 0 && (
                            <S.StudentDetail>
                              Absences: Week {enrollment.absenceWeeks.join(', ')}
                            </S.StudentDetail>
                          )}
                        </div>
                        {canManageCourseGroupStudents(currentRole) && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <S.SmallButton color="#F1AF59" onClick={() => openEditStudentModal(enrollment)}>
                              Edit
                            </S.SmallButton>
                            <S.SmallButton color="#10b981" onClick={() => openCreditModal(enrollment)}>
                              Credit
                            </S.SmallButton>
                            <S.SmallButton color="#ef4444" onClick={() => handleRemoveStudent(enrollment)}>
                              Remove
                            </S.SmallButton>
                          </div>
                        )}
                      </S.StudentItem>
                    ))}
                    {groupStudents.length === 0 && (
                      <S.EmptyState>No students enrolled in this course group</S.EmptyState>
                    )}
                  </S.StudentList>
                </S.Card>
              </>
            ) : (
              <S.EmptyState>Select a course group to view details</S.EmptyState>
            )}
          </S.DetailPanel>
          )}
        </S.SplitLayout>
      )}

      {/* Create / edit a course group. The same dialog the timetable opens, so a schedule
          written from either screen goes through one form and one set of validations. */}
      {(showCreateModal || (showEditModal && selectedGroup)) && (
        <CourseGroupFormModal
          mode={showEditModal ? 'edit' : 'create'}
          group={showEditModal ? selectedGroup : null}
          prefill={showCreateModal ? createPrefill : {}}
          onSaved={handleFormSaved}
          onClose={closeFormModal}
        />
      )}

      {showTermEndAdjust && (
        <TermEndAdjustModal
          campuses={termEndAdjustCampuses}
          onClose={() => setShowTermEndAdjust(false)}
          onDone={refreshCourseGroups}
        />
      )}

      {/* Student Modal */}
      {showStudentModal && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h3>{editingEnrollment ? 'Edit Student Enrollment' : 'Add Student to Course Group'}</h3>
              <S.CloseButton onClick={() => { setShowStudentModal(false); setEditingEnrollment(null); }}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              {!editingEnrollment && (
                <S.FormGroup>
                  <label>Student *</label>
                  {studentFormData.studentId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f3f4f6', borderRadius: '6px' }}>
                      <span style={{ flex: 1 }}>
                        {(() => {
                          const s = availableStudents.find(s => s.id === studentFormData.studentId);
                          return s ? `${s.name} - ${getYearLabel(s.grade)}` : studentFormData.studentId;
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setStudentFormData({ ...studentFormData, studentId: '' }); setStudentSearchQuery(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#6b7280', padding: '0 4px' }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <S.Input
                        type="text"
                        placeholder="Search student by name..."
                        value={studentSearchQuery}
                        onChange={(e) => setStudentSearchQuery(e.target.value)}
                        autoFocus
                      />
                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px' }}>
                        {availableStudents
                          .filter(s => {
                            if (!studentSearchQuery) return true;
                            const q = studentSearchQuery.toLowerCase();
                            return (s.name || '').toLowerCase().includes(q) || getYearLabel(s.grade).toLowerCase().includes(q);
                          })
                          .map(s => (
                            <div
                              key={s.id}
                              onClick={() => { setStudentFormData({ ...studentFormData, studentId: s.id }); setStudentSearchQuery(''); }}
                              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '14px' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              {s.name} - {getYearLabel(s.grade)}
                            </div>
                          ))
                        }
                        {availableStudents.filter(s => {
                          if (!studentSearchQuery) return true;
                          const q = studentSearchQuery.toLowerCase();
                          return (s.name || '').toLowerCase().includes(q) || getYearLabel(s.grade).toLowerCase().includes(q);
                        }).length === 0 && (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                            {studentsLoading ? 'Loading students...' : 'No matching students found'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </S.FormGroup>
              )}

              {editingEnrollment && (
                <p style={{ marginBottom: '16px' }}>
                  <strong>Student:</strong> {editingEnrollment.studentName}
                </p>
              )}

              <S.FormGroup>
                <label>Discount (%)</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {discountOptions.length > 0 && (
                    <S.FilterSelect
                      value=""
                      onChange={(e) => {
                        const preset = discountOptions.find(d => d.id === e.target.value);
                        if (preset) {
                          setStudentFormData({ ...studentFormData, discount: preset.value });
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select preset...</option>
                      {discountOptions.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name} ({d.value}%)
                        </option>
                      ))}
                    </S.FilterSelect>
                  )}
                  <S.Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={studentFormData.discount}
                    onChange={(e) => setStudentFormData({ ...studentFormData, discount: e.target.value })}
                    style={{ width: discountOptions.length > 0 ? '100px' : '100%' }}
                  />
                </div>
                <small style={{ color: '#6b7280' }}>
                  {discountOptions.length > 0
                    ? 'Select a preset or enter a custom discount percentage (0-100).'
                    : 'Percentage discount applied to credit per class (0-100).'}
                </small>
              </S.FormGroup>

              <S.FormGroup>
                <label>Start Date</label>
                <S.Input
                  type="date"
                  value={studentFormData.startDate}
                  onChange={(e) => setStudentFormData({ ...studentFormData, startDate: e.target.value })}
                />
                <small style={{ color: '#6b7280' }}>
                  When the student starts attending. Defaults to course start date.
                </small>
              </S.FormGroup>

              <S.FormGroup>
                <label>Absence Weeks (comma-separated)</label>
                <S.Input
                  type="text"
                  placeholder="e.g., 2, 4, 7"
                  value={studentFormData.absenceWeeks}
                  onChange={(e) => setStudentFormData({ ...studentFormData, absenceWeeks: e.target.value })}
                />
                <small style={{ color: '#6b7280' }}>
                  Weeks when the student was absent (not charged).
                </small>
              </S.FormGroup>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => { setShowStudentModal(false); setEditingEnrollment(null); }}>Cancel</S.Button>
              <S.Button
                color="#F1AF59"
                onClick={editingEnrollment ? handleUpdateEnrollment : handleAddStudent}
                disabled={actionLoading}
              >
                {actionLoading ? 'Saving...' : (editingEnrollment ? 'Save Changes' : 'Add Student')}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Credit Modal */}
      {showCreditModal && creditEnrollment && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h3>Adjust Credit</h3>
              <S.CloseButton onClick={() => { setShowCreditModal(false); setCreditEnrollment(null); setCreditDescription(''); setCreditFile(null); setUseStudentCredit(false); setTransferToStudent(false); }}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <p style={{ marginBottom: '16px' }}>
                <strong>Student:</strong> {creditEnrollment.studentName}
              </p>
              <p style={{ marginBottom: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
                <strong>Current Credit Balance:</strong> ${creditEnrollment.credit}
              </p>

              <S.FormGroup>
                <label>Action</label>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="creditAction"
                      value="add"
                      checked={creditAction === 'add'}
                      onChange={() => setCreditAction('add')}
                    />
                    Add Credit
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="creditAction"
                      value="subtract"
                      checked={creditAction === 'subtract'}
                      onChange={() => setCreditAction('subtract')}
                    />
                    Subtract Credit
                  </label>
                </div>
              </S.FormGroup>

              <S.FormGroup>
                <label>Amount ($)</label>
                <S.Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Enter amount"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                />
              </S.FormGroup>

              {creditAmount && parseFloat(creditAmount) > 0 && (
                <p style={{
                  padding: '12px',
                  background: creditAction === 'add' ? '#dcfce7' : '#fef3c7',
                  borderRadius: '6px',
                  color: creditAction === 'add' ? '#166534' : '#92400e',
                  marginBottom: '16px'
                }}>
                  New Balance: ${creditAction === 'add'
                    ? (parseFloat(creditEnrollment.credit) + parseFloat(creditAmount)).toFixed(2)
                    : Math.max(0, parseFloat(creditEnrollment.credit) - parseFloat(creditAmount)).toFixed(2)}
                </p>
              )}

              {/* Transfer options */}
              {creditAction === 'add' && (
                <S.FormGroup>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useStudentCredit}
                      onChange={(e) => setUseStudentCredit(e.target.checked)}
                      disabled={!creditEnrollment?.studentCredit || creditEnrollment.studentCredit <= 0}
                    />
                    Use Student Balance
                  </label>
                  <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Student balance: ${(creditEnrollment?.studentCredit || 0).toFixed(2)}
                    {(!creditEnrollment?.studentCredit || creditEnrollment.studentCredit <= 0) && ' (No balance available)'}
                  </small>
                </S.FormGroup>
              )}

              {creditAction === 'subtract' && (
                <S.FormGroup>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={transferToStudent}
                      onChange={(e) => setTransferToStudent(e.target.checked)}
                      disabled={!creditEnrollment?.remainingCredits || creditEnrollment.remainingCredits <= 0}
                    />
                    Transfer to Student Balance
                  </label>
                  <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Max transferable (remaining credit): ${(creditEnrollment?.remainingCredits || 0).toFixed(2)}
                    {(!creditEnrollment?.remainingCredits || creditEnrollment.remainingCredits <= 0) && ' (No remaining credit)'}
                  </small>
                </S.FormGroup>
              )}

              <S.FormGroup>
                <label>Description <span style={{ color: '#ef4444' }}>*</span></label>
                <S.TextArea
                  placeholder="Please describe the reason for this credit adjustment"
                  value={creditDescription}
                  onChange={(e) => setCreditDescription(e.target.value)}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Attachment (Optional)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setCreditFile(e.target.files[0] || null)}
                    style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  />
                  {creditFile && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#f3f4f6', borderRadius: '6px' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {creditFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCreditFile(null)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }}
                      >
                        &times;
                      </button>
                    </div>
                  )}
                  <small style={{ color: '#6b7280' }}>Supported formats: Images (JPG, PNG, etc.) and PDF</small>
                </div>
              </S.FormGroup>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => { setShowCreditModal(false); setCreditEnrollment(null); setCreditDescription(''); setCreditFile(null); setUseStudentCredit(false); setTransferToStudent(false); }}>Cancel</S.Button>
              <S.Button
                color={creditAction === 'add' ? '#10b981' : '#f59e0b'}
                onClick={handleAddCredit}
                disabled={actionLoading || !creditAmount || parseFloat(creditAmount) <= 0 || !creditDescription.trim()}
              >
                {actionLoading ? 'Processing...' : (creditAction === 'add' ? 'Add Credit' : 'Subtract Credit')}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Teacher Assignment Modal */}
      <TeacherAssignmentModal
        show={showTeacherModal}
        onClose={() => setShowTeacherModal(false)}
        courseGroup={selectedGroup}
        onUpdate={handleTeacherAdded}
        onReliefChange={handleReliefChange}
      />

      {/* Material Assignment Modal */}
      <MaterialAssignmentModal
        show={showMaterialModal}
        onClose={() => setShowMaterialModal(false)}
        courseGroup={selectedGroup}
        onUpdate={() => {}}
      />

      {/* Batch Student Modal */}
      <BatchStudentModal
        show={showBatchStudentModal}
        onClose={() => setShowBatchStudentModal(false)}
        courseGroup={selectedGroup}
        onUpdate={async () => {
          if (selectedGroup) {
            const students = await listCourseGroupStudents(selectedGroup.id);
            setGroupStudents(students);
          }
        }}
      />

      {/* Feedback Management Modal */}
      <FeedbackManagementModal
        show={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        courseGroup={selectedGroup}
        onUpdate={() => {}}
      />
    </S.Container>
  );
};

export default CourseGroups;
